import { ElementRef, forwardRef, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import useResizeObserver from '@react-hook/resize-observer';
import { debounce } from 'lodash-es';
import { theme } from '@frontend/theme';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { useZoomStore } from '../../store/zoom.store';
import { CalendarColumnBodyVariant, EventDropColumnStyleFn } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { ColumnEvents } from '../column-events';
import { EventDropOverlayContent } from '../event-drag-drop-context';
import { stripeBackgroundStyle } from '../styles';
import { GroupHourLines } from './group-hour-lines';

const useCalendarColumnResizeObserver = (shouldRegister: boolean) => {
  const firstColumnRef = useRef<ElementRef<'div'>>(null);
  const { setGroupColumnWidth } = useCalendarGridStore(['setGroupColumnWidth']);

  useResizeObserver(
    shouldRegister ? firstColumnRef : null,
    debounce((entry) => {
      const measuredWidth = Math.round(entry.contentRect.width);

      if (!measuredWidth || Number.isNaN(measuredWidth)) {
        return;
      }

      setGroupColumnWidth(measuredWidth);
    }, 100)
  );

  return firstColumnRef;
};

interface Props {
  isFirstGroup: boolean;
  columnIds: string[];
  groupId: string;
  startColumn: number;
  columnBodyVariant: CalendarColumnBodyVariant;
}

export const GroupBody = ({ isFirstGroup, columnIds, groupId, startColumn, columnBodyVariant }: Props) => {
  const shouldRegisterResizeObserver = isFirstGroup && columnBodyVariant === 'grid';
  const firstColumnRef = useCalendarColumnResizeObserver(shouldRegisterResizeObserver);
  const enableDroppableColumns = useCalendarGridStore((s) => s.enableDroppableColumns);

  return columnIds.map((columnId, index) => (
    <ColumnBody
      ref={index === 0 ? firstColumnRef : undefined}
      key={columnId}
      columnId={columnId}
      groupId={groupId}
      index={index}
      startColumn={startColumn}
      columnBodyVariant={columnBodyVariant}
      enableDroppableColumns={enableDroppableColumns}
    />
  ));
};

interface ColumnBodyProps {
  columnId: string;
  groupId: string;
  index: number;
  startColumn: number;
  columnBodyVariant: CalendarColumnBodyVariant;
  enableDroppableColumns: boolean;
}

const ColumnBody = forwardRef<HTMLDivElement, ColumnBodyProps>(
  ({ columnId, groupId, index, startColumn, columnBodyVariant, enableDroppableColumns }: ColumnBodyProps, ref) => {
    const dragStyles = useCalendarGridStore((s) => s.dragStyles);
    const eventDragPreview = useCalendarGridStore((s) => s.eventDragPreview);
    const eventsById = useCalendarGridStore((s) => s.eventsById);
    const isColumnReorderDragging = useCalendarGridStore((s) => s.isColumnReorderDragging);
    const isColumnReorderActive = useCalendarGridStore((s) => s.isColumnReorderActive);
    const hasSelectedEvent = useCalendarGridStore((s) => {
      return s.selectedEventIds?.length
        ? s.columnsById[columnId]?.eventIds.some((id) => s.selectedEventIds?.includes(id)) ?? false
        : false;
    });
    const pxPerMinute = useZoomStore((s) => s.pxPerMinute);
    const { setNodeRef, isOver } = useDroppable({
      id: columnId,
      data: { columnId, groupId },
      disabled: !enableDroppableColumns,
    });
    const isEventDropColumn = isOver || eventDragPreview?.columnId === columnId;

    const combinedRef = (node: HTMLDivElement) => {
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }
    };

    return (
      <div
        ref={combinedRef}
        data-column-id={columnId}
        data-group-id={groupId}
        data-drop-over={isOver ? 'true' : undefined}
        css={[
          columnStyle,
          columnBodyVariant === 'stacked'
            ? stackedColumnStyle
            : !isColumnReorderActive && !isColumnReorderDragging && stripeBackgroundStyle,
          isEventDropColumn &&
            (typeof dragStyles.eventDropColumn === 'function'
              ? (() => {
                  const ev = eventDragPreview?.eventId ? eventsById[eventDragPreview.eventId] : undefined;
                  return ev ? (dragStyles.eventDropColumn as EventDropColumnStyleFn)(ev, columnId, groupId) : undefined;
                })()
              : dragStyles.eventDropColumn),
        ]}
        style={{
          gridColumn: `${startColumn + index} / span 1`,
          borderInlineStartWidth: index === 0 ? 1 : 0,
          contentVisibility: hasSelectedEvent ? 'visible' : 'auto', // Native browser virtualization hint
        }}
      >
        {/* Hide event cards and hour lines while column headers are being reordered*/}
        {!isColumnReorderDragging && !isColumnReorderActive && (
          <>
            <ColumnEvents columnId={columnId} />
            <GroupHourLines />
            <CalendarDragStateContext.Provider
              value={{
                ...defaultCalendarDragState,
                isEventDropColumn: isEventDropColumn,
              }}
            >
              <EventDropOverlayContent
                isOver={isEventDropColumn}
                columnId={columnId}
                eventDragPreview={eventDragPreview}
                eventsById={eventsById}
                dragStyles={dragStyles}
                pxPerMinute={pxPerMinute}
              />
            </CalendarDragStateContext.Provider>
          </>
        )}
      </div>
    );
  }
);

ColumnBody.displayName = 'ColumnBody';

const columnStyle = css({
  gridRow: '4 / -2', // Spans all rows except the header, all-day, and spacer rows
  display: 'grid',
  position: 'relative',
  // Isolate each column's layout and style from siblings without clipping overflow.
  // content-visibility: auto was removed because it implicitly forces paint containment,
  // which clips absolutely-positioned children (drag handles, drop overlays, etc.)
  // that legitimately overflow the column boundary.
  // TODO: Investigate if we can re-enable content-visibility: auto without clipping overflow.
  contain: 'layout style',

  // z-index: 0 establishes a stacking context per column so that on hover (z-index: 1)
  // this column paints above its siblings. This allows the event card drag handle
  zIndex: 0,
  '&:hover': {
    zIndex: 1,
  },
  gridTemplateRows: 'repeat(var(--total-mins), var(--row-height))',

  borderInlineWidth: '0 1px',
  borderInlineColor: theme.colors.neutral20,
  borderInlineStyle: 'solid',
  scrollSnapAlign: 'start',
});

const stackedColumnStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(3),
  borderInline: 'none',
  padding: theme.spacing(3, 1),
});
